import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Calendar, Search, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import EpisodeList from "../components/browse/EpisodeList";
import { Skeleton } from "../components/ui/skeleton";
import {
  getPodcastFeed,
  getSubscriptions,
  subscribePodcast,
  unsubscribePodcast,
} from "../services/podcastApi";
import type { PodcastInfo, Episode, Subscription } from "../types/podcast";

const PAGE_SIZE = 20;

type DateFilter = "all" | "week" | "month" | "3months" | "year" | "custom";

function getDateFilterRange(filter: DateFilter, customStart?: string, customEnd?: string): { start: Date | null; end: Date | null } {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  
  switch (filter) {
    case "week": {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }
    case "month": {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }
    case "3months": {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 3);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }
    case "year": {
      const start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }
    case "custom": {
      return {
        start: customStart ? new Date(customStart + "T00:00:00") : null,
        end: customEnd ? new Date(customEnd + "T23:59:59") : null,
      };
    }
    default:
      return { start: null, end: null };
  }
}

// Downsize iTunes artwork URLs for faster loading
function getOptimizedArtwork(url: string): string {
  if (!url) return url;
  return url.replace(/\/\d+x\d+bb\.(jpg|png)$/i, "/200x200bb.$1");
}

export default function PodcastEpisodesPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Feed URL from query param: /browse/podcast?feed=https://...
  const feedUrl = searchParams.get("feed") || "";
  
  // Debug logging
  console.log("[PodcastEpisodesPage] feedUrl:", feedUrl, "authLoading:", authLoading);

  const [podcastInfo, setPodcastInfo] = useState<PodcastInfo | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [totalEpisodes, setTotalEpisodes] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isTogglingSubscription, setIsTogglingSubscription] = useState(false);
  const [_subscriptions, setSubscriptions] = useState<Subscription[]>([]);

  // Date filter state
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search query (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter episodes by date
  const filteredEpisodes = useMemo(() => {
    if (dateFilter === "all") return episodes;
    
    const { start, end } = getDateFilterRange(dateFilter, customStartDate, customEndDate);
    
    return episodes.filter((ep) => {
      if (!ep.published_date) return true; // Keep episodes without dates
      const epDate = new Date(ep.published_date);
      if (start && epDate < start) return false;
      if (end && epDate > end) return false;
      return true;
    });
  }, [episodes, dateFilter, customStartDate, customEndDate]);

  // Load podcast feed
  useEffect(() => {
    console.log("[PodcastEpisodesPage] useEffect triggered, feedUrl:", feedUrl, "search:", debouncedSearch);
    if (!feedUrl) {
      console.log("[PodcastEpisodesPage] No feedUrl, skipping fetch");
      return;
    }
    
    // When searching, show searching indicator instead of full loading skeleton
    if (debouncedSearch) {
      setIsSearching(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    console.log("[PodcastEpisodesPage] Fetching feed...");
    getPodcastFeed(feedUrl, PAGE_SIZE, 0, debouncedSearch || undefined)
      .then((data) => {
        console.log("[PodcastEpisodesPage] Got data:", data.podcast?.title, "episodes:", data.episodes.length);
        setPodcastInfo(data.podcast);
        setEpisodes(data.episodes);
        setTotalEpisodes(data.total_episodes);
        setOffset(data.episodes.length);
      })
      .catch((err) => {
        console.error("[PodcastEpisodesPage] Fetch error:", err);
        setError(err instanceof Error ? err.message : "Failed to load podcast feed");
      })
      .finally(() => {
        setIsLoading(false);
        setIsSearching(false);
      });
  }, [feedUrl, debouncedSearch]);

  // Check subscription status
  useEffect(() => {
    if (!user) return;
    getSubscriptions()
      .then((subs) => {
        setSubscriptions(subs);
        setIsSubscribed(subs.some((s) => s.feed_url === feedUrl));
      })
      .catch(() => {});
  }, [user, feedUrl]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);

    getPodcastFeed(feedUrl, PAGE_SIZE, offset, debouncedSearch || undefined)
      .then((data) => {
        setEpisodes((prev) => [...prev, ...data.episodes]);
        setOffset((prev) => prev + data.episodes.length);
      })
      .catch((err) => {
        console.error("Load more failed:", err);
      })
      .finally(() => setIsLoadingMore(false));
  }, [feedUrl, offset, isLoadingMore, debouncedSearch]);

  const handleToggleSubscription = useCallback(async () => {
    if (!podcastInfo) return;
    setIsTogglingSubscription(true);
    try {
      if (isSubscribed) {
        await unsubscribePodcast(feedUrl);
        setIsSubscribed(false);
        setSubscriptions((prev) => prev.filter((s) => s.feed_url !== feedUrl));
      } else {
        await subscribePodcast(
          feedUrl,
          podcastInfo.title,
          podcastInfo.artwork_url,
          podcastInfo.author,
        );
        setIsSubscribed(true);
      }
    } catch (err) {
      console.error("Subscription toggle failed:", err);
    } finally {
      setIsTogglingSubscription(false);
    }
  }, [feedUrl, podcastInfo, isSubscribed]);

  if (authLoading) return null;

  // No feed URL provided - show error
  if (!feedUrl) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <button
          onClick={() => navigate("/browse")}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Browse
        </button>
        <div className="py-16 text-center">
          <p className="text-sm font-medium text-gray-900">No podcast feed specified</p>
          <p className="mt-1 text-xs text-gray-500">Please select a podcast from the Browse page.</p>
          <button
            onClick={() => navigate("/browse")}
            className="mt-4 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
          >
            Browse Podcasts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      {/* Back link */}
      <button
        onClick={() => navigate("/browse")}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Browse
      </button>

      {/* Error state — full block, hides everything below */}
      {error && !isLoading && !podcastInfo && (
        <div className="py-16 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-900">
            This feed couldn't be loaded
          </p>
          <p className="mt-1 text-xs text-gray-500">
            The podcast feed may be temporarily unavailable or not supported.
          </p>
          <div className="mt-4 flex justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
            >
              Try again
            </button>
            <button
              onClick={() => navigate("/browse")}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Back to Browse
            </button>
          </div>
        </div>
      )}

      {/* Inline error (feed loaded partially but had issues) */}
      {error && podcastInfo && (
        <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
          <button
            onClick={() => window.location.reload()}
            className="ml-2 font-medium underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Podcast header */}
      {!error && isLoading ? (
        <div className="mb-8 flex items-start gap-5">
          <Skeleton className="h-28 w-28 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-60" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-28 rounded-md" />
          </div>
        </div>
      ) : podcastInfo ? (
        <div className="mb-8 flex items-start gap-5">
          <div className="h-28 w-28 shrink-0 overflow-hidden rounded-lg bg-gray-100">
            {podcastInfo.artwork_url ? (
              <img
                src={getOptimizedArtwork(podcastInfo.artwork_url)}
                alt={podcastInfo.title}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-gray-300">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-10 w-10"
                >
                  <path d="M19.952 1.651a.75.75 0 0 1 .298.599V16.303a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.403-4.909l2.311-.66a1.5 1.5 0 0 0 1.088-1.442V6.994l-9 2.572v9.737a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.402-4.909l2.31-.66a1.5 1.5 0 0 0 1.088-1.442V5.25a.75.75 0 0 1 .544-.721l10.5-3a.75.75 0 0 1 .456.122Z" />
                </svg>
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-gray-900">
              {podcastInfo.title}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {podcastInfo.author}
            </p>
            <p className="mt-0.5 text-xs text-gray-400">
              {totalEpisodes} episode{totalEpisodes !== 1 ? "s" : ""}
            </p>

            <button
              onClick={handleToggleSubscription}
              disabled={isTogglingSubscription}
              className={`mt-3 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                isSubscribed
                  ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {isTogglingSubscription && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              {isSubscribed ? "Unsubscribe" : "Subscribe"}
            </button>
          </div>
        </div>
      ) : null}

      {/* Search and Date filter */}
      {podcastInfo && !isLoading && (
        <div className="mb-4 space-y-3">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search episodes..."
              className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {isSearching ? (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
            ) : searchQuery ? (
              <button
                onClick={() => {
                  setSearchQuery("");
                  searchInputRef.current?.focus();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          {/* Date filter */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as DateFilter)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All episodes</option>
                <option value="week">Past week</option>
                <option value="month">Past month</option>
                <option value="3months">Past 3 months</option>
                <option value="year">Past year</option>
                <option value="custom">Custom range</option>
              </select>
            </div>

            {dateFilter === "custom" && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Start date"
                />
                <span className="text-xs text-gray-400">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="End date"
                />
              </div>
            )}

            {(dateFilter !== "all" || debouncedSearch) && (
              <span className="text-xs text-gray-500">
                {debouncedSearch 
                  ? `${filteredEpisodes.length} result${filteredEpisodes.length !== 1 ? 's' : ''} for "${debouncedSearch}"`
                  : `${filteredEpisodes.length} of ${episodes.length} episodes`
                }
              </span>
            )}
          </div>
        </div>
      )}

      {/* Episode list — hidden when feed completely failed */}
      {(podcastInfo || isLoading) && (
        <EpisodeList
          episodes={filteredEpisodes}
          podcastName={podcastInfo?.title ?? ""}
          podcastArtworkUrl={podcastInfo?.artwork_url}
          feedUrl={feedUrl}
          totalEpisodes={dateFilter === "all" ? totalEpisodes : filteredEpisodes.length}
          isLoading={isLoading}
          onLoadMore={dateFilter === "all" ? handleLoadMore : undefined}
          isLoadingMore={isLoadingMore}
        />
      )}
    </div>
  );
}
